import { ICellRendererParams } from 'ag-grid-community';
import { useEffect, useMemo, useState } from 'react';

import { IPartNote } from 'bom/components';
import { BomPartViewByType } from 'bom/enums';
import { useElementActions } from 'bom/hooks';
import { IElement, IElementComponentMap, IElementComponentNote } from 'bom/types';
import { NotesCompactView } from 'core/components';
import { NotificationPosition } from 'core/enums';
import { useAgGridClientTableContext, useNotificationContext } from 'core/states';
import { getAgGridTableCellData } from 'core/utils';
import { useJobPartNoteActions } from 'jobs/hooks';
import { IComponentNote, IJobPart } from 'jobs/types';
import { Variant } from 'theme/enums';

import {
  AddNoteButton,
  getMappedNotesArrByType,
  IJobPartNote,
  JobPartNoteType,
  mapElementNotesMapToManagedNotes,
} from '../AddNote';

interface IProps extends ICellRendererParams {
  isRowEditable: ((data: IJobPart) => boolean) | boolean;
  element?: IElement;
}

export const NotesCell = ({ isRowEditable = false, element, ...props }: IProps) => {
  const [isOpenManageNotes, setIsOpenManageNotes] = useState(false);

  const { clearNotification } = useNotificationContext();
  const { updateRows } = useAgGridClientTableContext();

  const { removeJobPartNotes, upsertJobPartNote } = useJobPartNoteActions({
    notificationPosition: NotificationPosition.DRAWER,
  });
  const { removeElementComponentNotes, upsertElementComponentNote } = useElementActions();

  const data = getAgGridTableCellData(props, 'notes');

  const mappedDefaultNotes = useMemo(
    () => getMappedNotesArrByType(data?.componentNotes || [], JobPartNoteType.DEFAULT),
    [data?.componentNotes]
  );

  const mappedElementNotes = useMemo(
    () =>
      mapElementNotesMapToManagedNotes(
        data?.elements || [],
        data?.elementComponentNotesMap || {},
        element
      ),
    [data?.elementComponentNotesMap, data?.elements, element]
  );

  const mappedCombinedNotes = useMemo(
    () => [...mappedDefaultNotes, ...mappedElementNotes],
    [mappedDefaultNotes, mappedElementNotes]
  );

  const compactNotes = useMemo(() => {
    const hasDefaultNotes = mappedDefaultNotes.length > 0;
    const hasElementNotes = mappedElementNotes.length > 0;

    if (hasDefaultNotes && hasElementNotes) {
      return [...mappedDefaultNotes.slice(0, 1), ...mappedElementNotes.slice(0, 1)];
    }

    if (hasDefaultNotes) {
      return mappedDefaultNotes.slice(0, 2);
    }

    if (hasElementNotes) {
      return mappedElementNotes.slice(0, 2);
    }

    return [];
  }, [mappedDefaultNotes, mappedElementNotes]);

  const onCellDoubleClicked = () => setIsOpenManageNotes(true);

  useEffect(() => {
    const handleDoubleClick = (eventParams) => {
      if (
        eventParams.node.rowIndex === props.node.rowIndex &&
        eventParams.column.colId === props.column.getColId()
      ) {
        onCellDoubleClicked();
      }
    };

    props.api.addEventListener('cellDoubleClicked', handleDoubleClick);

    return () => {
      !props.api.isDestroyed() &&
        props.api.removeEventListener('cellDoubleClicked', handleDoubleClick);
    };
  }, [props.api, props.column, props.node.rowIndex]);

  if (!data) {
    return props.value;
  }

  const editable = typeof isRowEditable === 'function' ? isRowEditable(data) : isRowEditable;

  const jobId = data.bomId;
  const componentId = data.componentId;

  const onSubmitEditNote = async (data: Partial<IJobPartNote>): Promise<{ success: boolean }> => {
    if (data.type === JobPartNoteType.ELEMENT) {
      const { success, elementComponentNote } = await upsertElementComponentNote({
        elementComponentNote: {
          content: data.content,
          id: data.id,
        },
      });

      if (success) {
        setTimeout(() => {
          updateJobPartWithElementNote(elementComponentNote);
          clearNotification();
        }, 1000);
      }

      return { success };
    }

    const { success, componentNote: partNote } = await upsertJobPartNote({
      bomId: jobId,
      bomPartViewBy: {
        group: BomPartViewByType.PART,
      },
      componentNote: {
        content: data.content,
        id: data.id,
      },
    });

    if (success) {
      setTimeout(() => {
        updateJobPartWithNote(partNote);
        clearNotification();
      }, 1000);
    }

    return { success };
  };

  const onDeleteNote = async (note: Partial<IJobPartNote>) => {
    if (note?.type === JobPartNoteType.ELEMENT) {
      const { success } = await removeElementComponentNotes({
        ids: [note.id],
      });

      if (!success) {
        return;
      }

      setTimeout(() => {
        updateJobPartWithElementNote(undefined, note.id);
        clearNotification();
      }, 1000);

      return;
    }

    const { success } = await removeJobPartNotes({
      bomId: jobId,
      bomPartViewBy: {
        group: BomPartViewByType.PART,
      },
      componentId,
      ids: [note.id],
    });

    if (!success) {
      return;
    }

    setTimeout(() => {
      updateJobPartWithNote(undefined, note.id);
      clearNotification();
    }, 1000);
  };

  const updateJobPartWithNote = (note?: IPartNote, deleteNoteId?: number) => {
    const prevNotes = data.componentNotes;

    let updatedNotes: IPartNote[] = [...prevNotes];

    if (deleteNoteId) {
      updatedNotes = updatedNotes.filter((note: IPartNote) => note.id !== deleteNoteId);
    }

    if (note) {
      updatedNotes = updatedNotes.map((existingNote: IPartNote) =>
        Number(existingNote.id) === Number(note.id) ? note : existingNote
      );
    }

    updateRows({
      update: [
        {
          ...data,
          componentNotes: updatedNotes,
        },
      ],
    });
  };

  const updateJobPartWithElementNote = (note?: IElementComponentNote, deleteNoteId?: number) => {
    const prevElementComponentNotes = data.elementComponentNotesMap;

    let updatedElementComponentNotes: { [elementId: string]: IComponentNote[] } = {
      ...prevElementComponentNotes,
    };

    if (deleteNoteId) {
      for (const [elemId, notes] of Object.entries(updatedElementComponentNotes)) {
        const noteIndex = notes.findIndex((note) => Number(note.id) === Number(deleteNoteId));

        if (noteIndex !== -1) {
          const filteredNotes = notes.filter((note) => Number(note.id) !== Number(deleteNoteId));

          if (filteredNotes.length > 0) {
            updatedElementComponentNotes[elemId] = filteredNotes;
          } else {
            delete updatedElementComponentNotes[elemId];
          }

          break;
        }
      }
    }

    if (note) {
      const elementId = (
        Object.values(data.elementComponentsMap).flat() as IElementComponentMap[]
      ).find(
        (elementComponent: IElementComponentMap) =>
          Number(elementComponent.id) === Number(note?.elementComponentId)
      )?.elementId;

      const updatedElementComponentNoteByElementId = updatedElementComponentNotes[elementId];

      updatedElementComponentNotes = {
        ...updatedElementComponentNotes,
        [elementId]: updatedElementComponentNoteByElementId
          ? updatedElementComponentNoteByElementId.map((existingNote: IComponentNote) =>
              Number(existingNote.id) === Number(note.id) ? note : existingNote
            )
          : [note],
      };
    }

    updateRows({
      update: [
        {
          ...data,
          elementComponentNotesMap: updatedElementComponentNotes,
        },
      ],
    });
  };

  return (
    <NotesCompactView
      compactPartNotes={compactNotes}
      totalNotes={mappedCombinedNotes}
      hasManageNotes={editable}
      manageNotesIsOpenExternal={isOpenManageNotes}
      updateManageNotesIsOpenExternal={setIsOpenManageNotes}
      addButton={<AddNoteButton jobPart={data} level={1} element={element} />}
      listAddButton={
        <AddNoteButton
          jobPart={data}
          element={element}
          buttonProps={{ startDecorator: null, variant: Variant.PLAIN }}
        />
      }
      noteProps={{ onSubmitEditNote, onDeleteNote }}
    />
  );
};
